import { createApi } from "@reduxjs/toolkit/query/react";
import {
  OrganizationData,
  OrganizationFields,
  OrganizationFilter,
  BankAccount,
  BankAccountFields,
} from "features/organization/types/organization";
import { baseAppQuery } from "shared/api/baseQuery";
import { snackbarOnQueryStarted } from "middleware/snackbarOnQueryStarted";

const ENDPOINT = "/organization";
const organizationsApi: any = createApi({
  reducerPath: "organizationsApi",
  baseQuery: baseAppQuery,
  keepUnusedDataFor: 0,
  refetchOnMountOrArgChange: true,
  tagTypes: ["organizations","orgBank"],
  endpoints: (builder) => ({
    getOrganizationList: builder.query<OrganizationData, OrganizationFilter>({
      query: (filter) => {
        const queryParams = new URLSearchParams();
        if (filter) {
          queryParams.set("page", filter.pageNumber.toString());
          queryParams.set("size", filter.pageSize.toString());
          if (filter.orgId) queryParams.set("orgId", filter.orgId);
          if (filter.orgName) queryParams.set("orgName", filter.orgName);
        }
        return `${ENDPOINT}/fetch-all?${queryParams}`;
      },
      providesTags: ["organizations","orgBank"],
    }),
    getOrganizationListCurrent: builder.query<OrganizationData, OrganizationFilter>({
      query: ()=>`${ENDPOINT}/fetch-all-current`,
      providesTags: ["organizations","orgBank"],
    }),
    getOrganizationById: builder.query<OrganizationData, string>({
      query: (id) => `${ENDPOINT}/${id}`,
    }),
    addOrganization: builder.mutation<OrganizationData, OrganizationFields>({
      query: (data) => ({
        url: ENDPOINT,
        method: "POST",
        body: data,
      }),
      invalidatesTags:["organizations"],
      onQueryStarted: snackbarOnQueryStarted({
        successMessage:"organizationDataCreated",
        errorMessage:"organizationDataNotCreated",
      }),
    }),
    updateOrganization: builder.mutation<OrganizationData, OrganizationFields>({
      query: (data) => ({
        url: ENDPOINT,
        method: "PUT",
        body: data,
      }),
      invalidatesTags:["organizations"],
      onQueryStarted: snackbarOnQueryStarted({
        successMessage:"organizationDataUpdated",
        errorMessage:"organizationDataNotUpdated",
      }),
    }),
    getBankAccountList: builder.query<Array<BankAccount>, string>({
      query: (organizationId: string) =>
        `${ENDPOINT}/bank-account/list?clientId=${organizationId}`,
      providesTags: ["orgBank"],
    }),
    addBankAccount: builder.mutation<BankAccount, BankAccountFields>({
      query: (data) => ({
        url: `${ENDPOINT}/bank-account`,
        method: "POST",
        body: data,
      }),
      invalidatesTags:["orgBank"],
      onQueryStarted: snackbarOnQueryStarted({
        successMessage:"bankAccountDataCreated",
        errorMessage:"bankAccountDataNotCreated",
      }),
    }),
    updateBankAccount: builder.mutation<BankAccount, BankAccountFields>({
      query: (data) => ({
        url: `${ENDPOINT}/bank-account`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags:["orgBank"],
      onQueryStarted: snackbarOnQueryStarted({
        successMessage:"bankAccountDataUpdated",
        errorMessage:"bankAccountDataNotUpdated",
      }),
    }),
    deleteBankAccount: builder.mutation<undefined, string>({
      query: (id) => ({
        url: `${ENDPOINT}/bank-account?id=${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["organizations","orgBank"],
      onQueryStarted: snackbarOnQueryStarted({
        successMessage:"deleteBankAccountSuccess",
        errorMessage:"deleteBankAccountFailed",
      }),
    }),
    activateBankAccount: builder.mutation<
      undefined,
      { id: string; orgId: string }
    >({
      query: ({ id, orgId }) => ({
        url: `${ENDPOINT}/bank-account/main?bankAccountId=${id}&clientId=${orgId}`,
        method: "POST",
      }),
    }),
  }),
});

export const {
  useGetOrganizationListQuery,
  useGetOrganizationListCurrentQuery,
  useGetOrganizationByIdQuery,
  useAddOrganizationMutation,
  useUpdateOrganizationMutation,
  useGetBankAccountListQuery,
  useAddBankAccountMutation,
  useUpdateBankAccountMutation,
  useDeleteBankAccountMutation,
  useActivateBankAccountMutation,
} = organizationsApi;
export default organizationsApi;
